import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldError, useForm } from 'react-hook-form'
import {
  CompoundDto,
  CreateQualityControlAssignmentDto,
  CustomQualityControlDto,
  CustomQualityControlType,
  QualityControlAssignmentDto,
  VisualQualityControlDto,
  VisualQualityControlType,
} from '@services/dtos'
import { qualityControlAssignmentSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  createQualityControlAssignment,
  updateQualityControlAssignment,
} from '@state/reducers/qualityControlAssignmentReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import { FormCrudHeader } from './FormCrudHeader'
import { CheckBoxInput } from '../inputs'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import CompoundForm from './CompoundForm'
import { compoundService } from '@services/CompoundService'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import { visualQualityControlService } from '@services/visualQualityControlService'
import VisualQualityControlForm from './VisualQualityControlForm'
import {
  getCustomQualityControls,
  setCustomQualityControlFilters,
} from '@state/reducers/customQualityControlReducer'
import { DEFAULT_FILTERS } from '@state/reducers/baseCrudReducer'
import { DataGridPro, GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Search } from 'lucide-react'
import { CustomQualityControlTypeLabels } from '@utils/constants'
import { UpTransition } from '../animations'
import CustomQualityControlForm from './CustomQualityControlForm'

type Props = {
  editingQualityControlAssignment: QualityControlAssignmentDto | null
  onClose: () => void
}

const QualityControlAssignmentForm: React.FC<Props> = ({
  editingQualityControlAssignment,
  onClose,
}) => {
  const editingColorQualityControlId =
    editingQualityControlAssignment?.visualQualityControls.find(
      ({ type }) => type === VisualQualityControlType.COLOR,
    )?.id
  const editingAppearanceQualityControlId =
    editingQualityControlAssignment?.visualQualityControls.find(
      ({ type }) => type === VisualQualityControlType.APPEARANCE,
    )?.id

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateQualityControlAssignmentDto>({
    defaultValues: editingQualityControlAssignment
      ? {
          compoundId: editingQualityControlAssignment.compound.id,
          customQualityControlIds:
            editingQualityControlAssignment.customQualityControls?.map(
              ({ id }) => id,
            ) ?? [],
          colorQualityControlId: editingColorQualityControlId,
          appearanceQualityControlId: editingAppearanceQualityControlId,
          radiochemicalPurityPercent:
            editingQualityControlAssignment.radiochemicalPurityPercent,
          radionuclidicPurityPercent:
            editingQualityControlAssignment.radionuclidicPurityPercent,
          phMinimum: editingQualityControlAssignment.phMinimum,
          phMaximum: editingQualityControlAssignment.phMaximum,
          colorIsRequired: editingQualityControlAssignment.colorIsRequired,
          appearanceIsRequired: editingQualityControlAssignment.appearanceIsRequired,
          radiochemicalPurityIsRequired: editingQualityControlAssignment.radiochemicalPurityIsRequired,
          radionuclidicPurityIsRequired: editingQualityControlAssignment.radionuclidicPurityIsRequired,
          phIsRequired: editingQualityControlAssignment.phIsRequired,
          requiredCustomQualityControlIds: editingQualityControlAssignment.requiredCustomQualityControlIds,
        } : {},
    resolver: yupResolver(qualityControlAssignmentSchema),
  })

  const dispatch = useAppDispatch()
  const { customQualityControls } = useAppSelector(
    ({ customQualityControl }) => ({
      customQualityControls: customQualityControl.items,
    }),
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [customQualityControlsSelected, setCustomQualityControlsSelected] =
    useState<CustomQualityControlDto[]>([])
  const [requiredCustomQcIds, setRequiredCustomQcIds] = useState<string[]>(
    editingQualityControlAssignment?.requiredCustomQualityControlIds ?? [],
  )
  const [customQualityControlFormOpen, setCustomQualityControlFormOpen] =
    useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    if (editingQualityControlAssignment?.customQualityControls) {
      setCustomQualityControlsSelected(
        editingQualityControlAssignment.customQualityControls,
      )
    }
    dispatch(setCustomQualityControlFilters({ limit: 10 }))
    dispatch(getCustomQualityControls())
    return () => {
      reset()
      dispatch(setCustomQualityControlFilters(DEFAULT_FILTERS))
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateQualityControlAssignmentDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () => cb('Une erreur est survenue')

    if (editingQualityControlAssignment) {
      dispatch(
        updateQualityControlAssignment({
          id: editingQualityControlAssignment.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('L\'affectation contrôle qualité a été modifiée avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createQualityControlAssignment(data))
        .unwrap()
        .then(() => {
          cb('L\'affectation contrôle qualité a été créée avec succès')
          onClose()
        })
        .catch(displayErrorMessage)
        .finally(() => setIsSubmitting(false))
    }
  }

  const fetchCompounds = async (search: string) => {
    const page = await compoundService.getAll({ search, isQualityControlConfigurable: true })
    const list = page?.datas ?? []
    return list
  }
  const fetchVisualQualityControls = async (
    search: string,
    type: VisualQualityControlType,
  ) => {
    const page = await visualQualityControlService.getAll({ search, type })
    const list = page?.datas ?? []
    return list
  }

  const { editingColorControl, editingAppearanceControl } = useMemo(() => {
    const controls =
      editingQualityControlAssignment?.visualQualityControls || []

    return {
      editingColorControl: controls.find(
        ({ type }) => type === VisualQualityControlType.COLOR,
      ),
      editingAppearanceControl: controls.find(
        ({ type }) => type === VisualQualityControlType.APPEARANCE,
      ),
    }
  }, [editingQualityControlAssignment?.visualQualityControls])

  // setTimeout : fix No row with id #[...] found
  const removeCustomQualityControl = (id: GridRowId) => {
    setTimeout(() => {
      setCustomQualityControlsSelected((prevRows) =>
        prevRows.filter((row) => row.id !== id),
      )
      setRequiredCustomQcIds((prev) => prev.filter((i) => i !== id))
    })
  }
  const customQualityControlsColumns: GridColumns = [
    {
      field: 'actions',
      headerName: '',
      width: 60,
      renderCell: ({ row }) => {
        const isSelected = customQualityControlsSelected
          .map(({ id }) => id)
          .includes(row.id)
        return (
          <Tooltip
            title={
              isSelected ? 'Retirer de la sélection' : 'Ajouter à la sélection'
            }
            arrow
          >
            <IconButton
              onClick={() =>
                isSelected
                  ? removeCustomQualityControl(row.id)
                  : setCustomQualityControlsSelected((prev) => [...prev, row])
              }
            >
              {isSelected ? (
                <CheckBoxIcon color="primary" />
              ) : (
                <CheckBoxOutlineBlankIcon />
              )}
            </IconButton>
          </Tooltip>
        )
      },
    },
    { field: 'code', headerName: 'Code', width: 60 },
    { field: 'label', headerName: 'Libellé', width: 150 },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      valueGetter: (params) => CustomQualityControlTypeLabels[params.row.type],
    },
    {
      field: 'expectedResult',
      headerName: 'Valeur attendue',
      valueGetter: (params) => {
        switch (params.row.type) {
          case CustomQualityControlType.LIST:
            return params.row.expectedOption?.label || ''
          case CustomQualityControlType.BOOLEAN:
            return params.row.expected ? 'Oui' : 'Non'
          case CustomQualityControlType.TEXT:
            return params.row.expectedValue || ''
          default:
            return ''
        }
      },
      width: 150,
    },
    { field: 'unit', headerName: 'Unité', width: 100 },
    {
      field: 'isRequired',
      headerName: 'Obligatoire',
      width: 110,
      renderCell: ({ row }) => {
        const isSelected = customQualityControlsSelected.map(({ id }) => id).includes(row.id)
        if (!isSelected) return null
        return (
          <Switch
            size="small"
            checked={requiredCustomQcIds.includes(row.id)}
            onChange={(e) => {
              setRequiredCustomQcIds((prev) =>
                e.target.checked ? [...prev, row.id] : prev.filter((i) => i !== row.id),
              )
            }}
          />
        )
      },
    },
  ]

  const handlePreSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setValue(
      'customQualityControlIds',
      customQualityControlsSelected.map(({ id }) => id),
    )
    setValue('requiredCustomQualityControlIds', requiredCustomQcIds)
    handleSubmit(onSubmitForm)()
  }

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handlePreSubmit} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingQualityControlAssignment}
        />

        <div className="w-full mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Affectation contrôle qualité
            </legend>

            <Grid container columnSpacing={2} mt={1}>
              <Grid container item xs={12} md={5} spacing={2}>
                <Grid item xs={12}>
                  <AddWrapperInput
                    legend="MRP"
                    addTooltipTitle="Ajouter un MRP"
                    dialogContent={(close) => (
                      <CompoundForm
                        onClose={() => {
                          close()
                        }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<CompoundDto>
                        fetchService={fetchCompounds}
                        onChange={(value: CompoundDto | null) => {
                          setValue('compoundId', value?.id ?? '')
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const compound = value as CompoundDto
                          return compound ? compound.name : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un MRP"
                        label="MRP"
                        helperText={errors.compoundId?.message}
                        error={!!errors.compoundId}
                        minSearchLength={0}
                        initialOptions={
                          editingQualityControlAssignment?.compound
                            ? [editingQualityControlAssignment.compound]
                            : []
                        }
                        value={
                          editingQualityControlAssignment?.compound ?? null
                        }
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AddWrapperInput
                    legend="Couleur"
                    addTooltipTitle="Ajouter un contrôle qualité visuel"
                    dialogContent={(close) => (
                      <VisualQualityControlForm
                        onClose={close}
                        defaultValues={{ type: VisualQualityControlType.COLOR }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<VisualQualityControlDto>
                        autoFetchOnMount
                        fetchService={(search) =>
                          fetchVisualQualityControls(
                            search,
                            VisualQualityControlType.COLOR,
                          )
                        }
                        onChange={(value: VisualQualityControlDto | null) => {
                          setValue('colorQualityControlId', value?.id ?? '')
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const colorControl = value as VisualQualityControlDto
                          return colorControl ? colorControl.label : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un contrôle couleur"
                        label="Couleur"
                        helperText={errors.colorQualityControlId?.message}
                        error={!!errors.colorQualityControlId}
                        minSearchLength={0}
                        initialOptions={
                          editingColorControl ? [editingColorControl] : []
                        }
                        value={editingColorControl ?? null}
                      />
                    }
                  />
                  <FormControlLabel
                    control={<Switch size="small" {...(control.register('colorIsRequired'))} defaultChecked={!!editingQualityControlAssignment?.colorIsRequired} />}
                    label="Obligatoire"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <AddWrapperInput
                    legend="Aspect"
                    addTooltipTitle="Ajouter un contrôle qualité visuel"
                    dialogContent={(close) => (
                      <VisualQualityControlForm
                        onClose={close}
                        defaultValues={{ type: VisualQualityControlType.APPEARANCE }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<VisualQualityControlDto>
                        autoFetchOnMount
                        fetchService={(search) =>
                          fetchVisualQualityControls(
                            search,
                            VisualQualityControlType.APPEARANCE,
                          )
                        }
                        onChange={(value: VisualQualityControlDto | null) => {
                          setValue(
                            'appearanceQualityControlId',
                            value?.id ?? '',
                          )
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const appearanceControl =
                            value as VisualQualityControlDto
                          return appearanceControl
                            ? appearanceControl.label
                            : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un contrôle aspect"
                        label="Aspect"
                        helperText={errors.appearanceQualityControlId?.message}
                        error={!!errors.appearanceQualityControlId}
                        minSearchLength={0}
                        initialOptions={
                          editingAppearanceControl
                            ? [editingAppearanceControl]
                            : []
                        }
                        value={editingAppearanceControl ?? null}
                      />
                    }
                  />
                  <FormControlLabel
                    control={<Switch size="small" {...(control.register('appearanceIsRequired'))} defaultChecked={!!editingQualityControlAssignment?.appearanceIsRequired} />}
                    label="Obligatoire"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputField control={control} name="radiochemicalPurityPercent" label="Pureté radiopharmaceutique (%)" type="number" />
                  <FormControlLabel
                    control={<Switch size="small" {...(control.register('radiochemicalPurityIsRequired'))} defaultChecked={!!editingQualityControlAssignment?.radiochemicalPurityIsRequired} />}
                    label="Obligatoire"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField control={control} name="radionuclidicPurityPercent" label="Pureté radionuclidique (%)" type="number" />
                  <FormControlLabel
                    control={<Switch size="small" {...(control.register('radionuclidicPurityIsRequired'))} defaultChecked={!!editingQualityControlAssignment?.radionuclidicPurityIsRequired} />}
                    label="Obligatoire"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField control={control} name="phMinimum" label="pH minimum" type="number" />
                  <FormControlLabel
                    control={<Switch size="small" {...(control.register('phIsRequired'))} defaultChecked={!!editingQualityControlAssignment?.phIsRequired} />}
                    label="Obligatoire"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField control={control} name="phMaximum" label="pH maximum" type="number" />
                </Grid>
              </Grid>

              <Grid container item xs={12} md={7} direction="column">
                {errors.customQualityControlIds && (
                  <FormHelperText error>
                    {
                      (errors.customQualityControlIds as unknown as FieldError)
                        .message
                    }
                  </FormHelperText>
                )}

                <Box display="flex" alignItems="center" className="mb-2">
                  <TextField
                    label="Recherche contrôle qualité personnalisé"
                    placeholder="Rechercher un libellé ou code"
                    onChange={(e) => {
                      dispatch(
                        setCustomQualityControlFilters({
                          search: e.target.value,
                          page: 0,
                        }),
                      )
                      dispatch(getCustomQualityControls())
                    }}
                    InputProps={{
                      endAdornment: (
                        <Search className="text-gray-500" size={20} />
                      ),
                    }}
                    className="w-full rounded-xl bg-gray-50 transition-all duration-200 hover:bg-gray-100 focus:bg-white"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgb(229, 231, 235)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0 0 2px rgb(229 231 235)',
                        },
                      },
                    }}
                  />

                  <span className="inline-flex items-center">
                    <Tooltip
                      title="Ajouter un contrôle qualité personnalisé"
                      arrow
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setCustomQualityControlFormOpen(true)}
                        sx={{
                          height: 55,
                          paddingLeft: 1.5,
                          paddingRight: 1.5,
                          minWidth: 'auto',
                          width: 'auto',
                          borderColor: '#e5e7eb',
                        }}
                      >
                        <i className="fas fa-plus"></i>
                      </Button>
                    </Tooltip>
                  </span>
                </Box>

                <DataGridPro
                  density="standard"
                  style={{
                    minHeight: 300,
                    height: '48.7vh',
                    backgroundColor: 'white',
                    borderRadius: '15px 15px 25px 25px',
                  }}
                  headerHeight={26}
                  rowHeight={36}
                  className="mb-4"
                  columns={customQualityControlsColumns}
                  rows={customQualityControls.datas}
                  rowCount={customQualityControls.totalCount}
                  disableSelectionOnClick
                  pagination
                  paginationMode="server"
                  disableMultipleSelection
                  pageSize={10}
                  onPageChange={(page) => {
                    dispatch(setCustomQualityControlFilters({ page }))
                    dispatch(getCustomQualityControls())
                  }}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>

      {customQualityControlFormOpen && (
        <Dialog
          open={customQualityControlFormOpen}
          onClose={() => setCustomQualityControlFormOpen(false)}
          TransitionComponent={UpTransition}
          slotProps={{ backdrop: {} }}
        >
          <DialogContent>
            {customQualityControlFormOpen && (
              <CustomQualityControlForm
                onClose={() => setCustomQualityControlFormOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default QualityControlAssignmentForm
